/**
 * Event Tracker
 * Unified API for tracking events across GA4, PostHog, and Supabase
 */

import * as ga4 from './ga4.js';
import * as posthog from './posthog.js';
import * as customAnalytics from './custom-analytics.js';
import { isDevelopment } from './utils.js';

/**
 * Track an event across all analytics platforms
 * @param {string} eventName - Event name
 * @param {Object} options - Event options
 * @param {string} options.category - Event category for Supabase
 * @param {Object} options.properties - Event properties
 * @param {Object} options.context - Additional context (visualizerType, trackCount, etc.)
 * @param {boolean} options.ga4 - Track in GA4 (default: true)
 * @param {boolean} options.posthog - Track in PostHog (default: true)
 * @param {boolean} options.supabase - Track in Supabase (default: true)
 */
export function track(eventName, options = {}) {
  const {
    category = 'engagement',
    properties = {},
    context = {},
    ga4: trackGA4 = true,
    posthog: trackPostHog = true,
    supabase: trackSupabase = true
  } = options;

  if (isDevelopment()) {
    console.log('[EventTracker] Event:', eventName, { category, properties, context });
  }

  // Track in GA4
  if (trackGA4) {
    ga4.trackEvent(eventName, properties);
  }

  // Track in PostHog
  if (trackPostHog) {
    posthog.capture(eventName, properties);
  }

  // Track in Supabase (async, fire-and-forget)
  if (trackSupabase) {
    customAnalytics.trackEvent(eventName, category, properties, context).catch(() => {});
  }
}

/**
 * Track conversion event across all platforms
 * @param {string} conversionType - Type of conversion
 * @param {Object} details - Conversion details
 */
export function trackConversion(conversionType, details = {}) {
  const eventName = `conversion_${conversionType}`;

  if (isDevelopment()) {
    console.log('[EventTracker] Conversion:', conversionType, details);
  }

  // Build properties for GA4/PostHog
  const properties = {
    conversion_type: conversionType,
    ...details
  };

  // Track in GA4
  ga4.trackEvent(eventName, properties);

  // Track in PostHog
  posthog.capture(eventName, properties);

  // Track in Supabase conversions table
  customAnalytics.trackConversion(conversionType, details).catch(() => {});
}

// =============================================================================
// PRE-DEFINED EVENTS
// =============================================================================

/**
 * Track app loaded event
 * @param {Object} context - App context
 */
export function trackAppLoaded(context = {}) {
  track('app_loaded', {
    category: 'session',
    properties: {
      visualizer_count: context.visualizerCount || 0,
      device_type: context.deviceType || 'unknown'
    },
    context
  });
}

/**
 * Track files loaded event
 * @param {number} fileCount - Number of files loaded
 * @param {number} totalDuration - Total duration in seconds
 */
export function trackFilesLoaded(fileCount, totalDuration) {
  track('files_loaded', {
    category: 'engagement',
    properties: {
      file_count: fileCount,
      total_duration: Math.round(totalDuration)
    },
    context: {
      trackCount: fileCount
    }
  });
}

/**
 * Track visualizer selected
 * @param {string} visualizerType - Visualizer type
 * @param {boolean} isPremium - Whether it's a premium visualizer
 */
export function trackVisualizerSelected(visualizerType, isPremium = false) {
  track('visualizer_selected', {
    category: 'engagement',
    properties: {
      visualizer_type: visualizerType,
      is_premium: isPremium
    },
    context: {
      visualizerType
    }
  });
}

/**
 * Track playback started
 * @param {number} trackDuration - Track duration in seconds
 * @param {boolean} hasLyrics - Whether the track has lyrics
 */
export function trackPlaybackStarted(trackDuration, hasLyrics = false) {
  track('playback_started', {
    category: 'engagement',
    properties: {
      track_duration: Math.round(trackDuration),
      has_lyrics: hasLyrics
    }
  });
}

/**
 * Track export initiated
 * @param {Object} exportConfig - Export configuration
 */
export function trackExportInitiated(exportConfig = {}) {
  track('export_initiated', {
    category: 'conversion',
    properties: {
      resolution: exportConfig.resolution,
      fps: exportConfig.fps,
      duration_seconds: exportConfig.durationSeconds,
      track_count: exportConfig.trackCount,
      visualizer_type: exportConfig.visualizerType
    },
    context: {
      visualizerType: exportConfig.visualizerType,
      trackCount: exportConfig.trackCount
    }
  });
}

/**
 * Track export completed
 * @param {Object} exportResult - Export result
 */
export function trackExportCompleted(exportResult = {}) {
  track('export_completed', {
    category: 'conversion',
    properties: {
      file_size_mb: exportResult.fileSizeMb,
      export_duration_ms: exportResult.exportDurationMs,
      resolution: exportResult.resolution,
      fps: exportResult.fps
    },
    context: {
      visualizerType: exportResult.visualizerType
    }
  });

  // Also track as conversion
  trackConversion('export_success', {
    properties: {
      file_size_mb: exportResult.fileSizeMb,
      resolution: exportResult.resolution
    }
  });
}

/**
 * Track paywall shown
 * @param {string} feature - Feature that triggered paywall
 * @param {string} triggerLocation - Where in the app
 */
export function trackPaywallShown(feature, triggerLocation) {
  track('paywall_shown', {
    category: 'conversion',
    properties: {
      feature,
      trigger_location: triggerLocation
    }
  });
}

/**
 * Track signup completed
 * @param {string} method - Signup method (email, google, etc.)
 */
export function trackSignupCompleted(method = 'email') {
  track('signup_completed', {
    category: 'conversion',
    properties: {
      method
    }
  });

  // Also track as conversion
  trackConversion('signup', {
    properties: { method },
    triggerLocation: 'auth_modal'
  });
}

/**
 * Track login completed
 * @param {string} method - Login method
 */
export function trackLoginCompleted(method = 'email') {
  track('login_completed', {
    category: 'session',
    properties: {
      method
    }
  });
}

/**
 * Track pricing modal opened
 * @param {string} triggerLocation - Where in the app it was triggered
 */
export function trackPricingModalOpened(triggerLocation = 'unknown') {
  track('pricing_modal_opened', {
    category: 'monetization',
    properties: {
      trigger_location: triggerLocation
    }
  });
}

/**
 * Track checkout started
 * @param {string} tier - Tier being purchased
 * @param {string} billingPeriod - monthly or annual
 * @param {number} price - Price in dollars
 */
export function trackCheckoutStarted(tier, billingPeriod, price) {
  track('checkout_started', {
    category: 'monetization',
    properties: {
      tier,
      billing_period: billingPeriod,
      price
    }
  });
}

/**
 * Track tier upgrade
 * @param {string} fromTier - Previous tier
 * @param {string} toTier - New tier
 * @param {number} revenueCents - Revenue in cents
 */
export function trackTierUpgrade(fromTier, toTier, revenueCents = null) {
  trackConversion('tier_upgrade', {
    fromTier,
    toTier,
    revenueCents,
    triggerLocation: 'checkout'
  });
}

/**
 * Identify user after authentication
 * @param {string} userId - User ID
 * @param {Object} properties - User properties
 */
export function identifyUser(userId, properties = {}) {
  // Identify in PostHog
  posthog.identify(userId, properties);

  // Set user ID in GA4
  ga4.setUserId(userId);

  // Link analytics IDs to user profile in Supabase
  customAnalytics.linkAnalyticsToUser().catch(() => {});
}

/**
 * Reset user identity (on logout)
 */
export function resetUser() {
  posthog.reset();
}
